import ResultDetailRow from './ResultDetailRow';
import RetryButton from './RetryButton';
import { Calendar, CheckCircle2, XCircle, Timer, Brain, MessageCircle, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { updateWhatsAppStatus } from '../../services/questionnaireService';
import { useNavigate } from 'react-router-dom';

const ResultDetails = ({ resultData }) => {
  const [whatsappStatus, setWhatsappStatus] = useState(resultData.whatsapp_status || 'not_requested');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const correctPercentage = Math.round((resultData.correct_answers_count / resultData.total_questions) * 100);
  const timeSpent = resultData.formatted_time_spent || "12:30";
  
  const getPerformanceLevel = (percentage) => {
    if (percentage >= 90) return { level: "عالی", color: "text-success" };
    if (percentage >= 70) return { level: "خوب", color: "text-info" };
    if (percentage >= 50) return { level: "متوسط", color: "text-warning" };
    return { level: "نیاز به تلاش بیشتر", color: "text-error" };
  };
  
  const performance = getPerformanceLevel(correctPercentage);

  const handleWhatsAppRequest = async () => {
    if (!window.confirm('آیا مایل به دریافت نتیجه در واتس اپ هستید؟')) {
      return;
    }

    setIsLoading(true);
    try {
      await updateWhatsAppStatus(resultData.submission_id);
      toast.success('درخواست شما با موفقیت ثبت شد', {
        position: 'top-right',
        autoClose: 2000,
      });
      // Navigate to home page after 2 seconds
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      if (error.message.includes('دسترسی غیرمجاز')) {
        navigate('/login');
      } else {
        toast.error(error.message, {
          position: 'top-right',
          autoClose: 3000,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getWhatsAppButtonContent = () => {
    switch (whatsappStatus) {
      case 'not_requested':
        return {
          text: 'دریافت نتایج در واتس اپ',
          icon: <MessageCircle size={20} />,
          disabled: false,
          className: 'theme-button'
        };
      case 'sent':
        return {
          text: 'نتیجه ارسال شده است',
          icon: <CheckCircle2 size={20} className="text-success" />,
          disabled: true,
          className: 'bg-[rgb(var(--form-bg))] text-[rgb(var(--text-secondary))]'
        };
      default:
        return {
          text: 'دریافت نتایج در واتس اپ',
          icon: <MessageCircle size={20} />,
          disabled: false,
          className: 'theme-button'
        };
    }
  };

  const buttonContent = getWhatsAppButtonContent();

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-3">نتیجه آزمون</h1>
        <p className="text-secondary">{resultData.questionnaire_title}</p>
      </div>

      <div className="relative p-6 rounded-xl theme-card mb-8">
        <div className="text-center">
          <div className="text-5xl font-bold mb-2">
            {correctPercentage}%
          </div>
          <div className={`text-xl font-semibold ${performance.color}`}>
            {performance.level}
          </div>
        </div>
      </div>

      <div className="grid gap-4">
        <ResultDetailRow 
          icon={<Timer size={20} />}
          label="زمان صرف شده" 
          value={timeSpent}
          valueClass="text-info font-semibold"
        />

        <ResultDetailRow 
          icon={<Calendar size={20} />}
          label="تاریخ آزمون" 
          value={new Date(resultData.submitted_at).toLocaleDateString('fa-IR')}
          valueClass="text-secondary"
        />
        
        {resultData.skill_scores && (
          <div className="mt-6 space-y-4">
            <h3 className="text-xl font-semibold mb-4">امتیاز مهارت‌ها</h3>
            {Object.entries(resultData.skill_scores).map(([skill, score]) => (
              <ResultDetailRow 
                key={skill}
                icon={<Brain size={20} />}
                label={skill} 
                value={`${score}%`}
                valueClass={`font-semibold ${score >= 70 ? 'text-success' : 'text-warning'}`}
              />
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-secondary pt-6 mt-8">
        <div className="flex flex-col space-y-4">
          <RetryButton />
          <button 
            className={`w-full px-6 py-3 ${buttonContent.className} rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
            onClick={handleWhatsAppRequest}
            disabled={buttonContent.disabled || isLoading}
          >
            {isLoading ? <Loader2 size={20} className="animate-spin" /> : buttonContent.icon}
            {buttonContent.text}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResultDetails;

